import { useState, type Key } from 'react';
import {
  Table,
  Button,
  Tag,
  Typography,
  Space,
  Modal,
  Form,
  Input,
  Select,
  AutoComplete,
  Popconfirm,
  App,
} from 'antd';
import { PlusOutlined, MinusCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { LIST_PAGINATION, LIST_SCROLL_Y } from '../../lib/tablePagination';
import {
  useTenants,
  useCreateTenant,
  useUpdateTenant,
  useDeleteTenant,
  type TenantListItem,
} from '../../api/tenants';
import { useUsers } from '../../api/users';
import { useContactGroups } from '../../api/contact-groups';
import { useFactoryNameOptions } from '../../api/factory-names';
import { useSessionStore } from '../../stores/sessionStore';
import type { MemberItem } from '../../api/members';
import { fmtTime } from '../../lib/format';
import { api, ApiError } from '../../lib/apiClient';
import { ExcelImportButton } from '../../components/ExcelImportButton';
import {
  parseTenantFullSheet,
  exportRowsToXlsx,
  TENANT_FULL_TEMPLATE_HEADERS,
  TENANT_FULL_TEMPLATE_SAMPLE,
  type TenantFullImportRow,
} from '../../lib/excel';

const ROLE_CN: Record<string, string> = { admin: '管理员', editor: '编辑', viewer: '审核员' };

const MEMBER_ROLE_OPTIONS = [
  { value: 'admin', label: '管理员' },
  { value: 'editor', label: '编辑' },
  { value: 'viewer', label: '审核员' },
];

export function TenantsPage() {
  const { message } = App.useApp();
  const currentTenantId = useSessionStore((s) => s.currentTenant?.id);
  const clearTenant = useSessionStore((s) => s.clearTenant);
  const tenants = useTenants();
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant();
  const deleteTenant = useDeleteTenant();
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const users = useUsers(); // 建产品选人 + 导入成员按邮箱匹配都需要
  const groups = useContactGroups(createOpen);
  const factoryOptions = useFactoryNameOptions();
  const emailToUser = new Map((users.data ?? []).map((u) => [u.email.toLowerCase(), u]));
  const [editTenant, setEditTenant] = useState<TenantListItem | null>(null);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [delDone, setDelDone] = useState(0);

  const onBulkDelete = async () => {
    setBulkDeleting(true);
    setDelDone(0);
    let done = 0;
    const results = await Promise.allSettled(
      selectedKeys.map((tid) =>
        deleteTenant.mutateAsync(tid).finally(() => {
          done += 1;
          setDelDone(done);
        }),
      ),
    );
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const fail = results.length - ok;
    setBulkDeleting(false);
    // 删掉的若含当前所在产品，清掉会话残留，避免仍停留在已删产品
    if (currentTenantId && selectedKeys.includes(currentTenantId)) clearTenant();
    setSelectedKeys([]);
    if (fail === 0) message.success(`已删除 ${ok} 个产品及其全部数据`);
    else message.warning(`删除 ${ok} 个，${fail} 个失败`);
  };

  const onCreate = async () => {
    const v = await createForm.validateFields();
    const {
      members = [],
      group_ids = [],
      ...tenantInput
    } = v as {
      code: string;
      name: string;
      customer_company: string;
      members?: { user_id?: string; role?: 'admin' | 'editor' | 'viewer' }[];
      group_ids?: string[];
    };

    setCreating(true);
    let id: string;
    try {
      ({ id } = await createTenant.mutateAsync(tenantInput));
    } catch (e) {
      setCreating(false);
      message.error(e instanceof ApiError && e.status === 409 ? '编码已存在' : '创建失败');
      return;
    }

    // 产品已建好；成员/联系人为「尽力而为」，失败只提示不回滚
    const fails: string[] = [];
    const validMembers = members.filter((m) => m?.user_id && m?.role);
    for (const m of validMembers) {
      try {
        await api.post(`/tenants/${id}/members`, { user_id: m.user_id, role: m.role });
      } catch {
        const u = users.data?.find((x) => x.id === m.user_id);
        fails.push(`成员 ${u?.name ?? u?.email ?? m.user_id}`);
      }
    }
    for (const gid of group_ids) {
      try {
        await api.post(`/tenants/${id}/contacts/import-group`, { group_id: gid });
      } catch {
        const g = groups.data?.find((x) => x.id === gid);
        fails.push(`联系人分组 ${g?.name ?? gid}`);
      }
    }

    setCreating(false);
    setCreateOpen(false);
    createForm.resetFields();
    if (fails.length === 0) {
      message.success(
        `产品已创建${validMembers.length ? `，添加 ${validMembers.length} 名成员` : ''}${
          group_ids.length ? `，导入 ${group_ids.length} 个联系人分组` : ''
        }`,
      );
    } else {
      message.warning(`产品已创建，但以下未成功：${fails.join('、')}（可进入产品后再补）`);
    }
  };

  // 导出产品 + 各产品成员（每个成员一行；无成员的产品占一行）
  const onExportTenants = async () => {
    const list = tenants.data ?? [];
    if (list.length === 0) {
      message.warning('暂无产品可导出');
      return;
    }
    setExporting(true);
    try {
      const withMembers = await Promise.all(
        list.map(async (t) => {
          try {
            const r = await api.get<{ members: MemberItem[] }>(`/tenants/${t.tenant_id}/members`);
            return { t, members: r.members ?? [] };
          } catch {
            return { t, members: [] as MemberItem[] };
          }
        }),
      );
      const rows: string[][] = [];
      for (const { t, members } of withMembers) {
        const base = [
          t.code ?? '',
          t.name ?? '',
          t.customer_company ?? '',
          t.status === 'active' ? '启用' : '归档',
        ];
        if (members.length === 0) {
          rows.push([...base, '', '']);
        } else {
          for (const m of members) {
            rows.push([...base, m.email || '', ROLE_CN[m.role] ?? m.role]);
          }
        }
      }
      exportRowsToXlsx(
        `产品列表_${new Date().toISOString().slice(0, 10)}`,
        ['编码', '名称', '生产工厂', '状态', '成员邮箱', '成员角色'],
        rows,
      );
    } finally {
      setExporting(false);
    }
  };

  const onEdit = () =>
    editForm.validateFields().then((v) => {
      if (!editTenant) return;
      updateTenant.mutate(
        { tid: editTenant.tenant_id, body: v },
        {
          onSuccess: () => {
            message.success('已更新');
            setEditTenant(null);
          },
          onError: () => message.error('更新失败'),
        },
      );
    });

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Space style={{ justifyContent: 'space-between', width: '100%' }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          产品管理
        </Typography.Title>
        <Space>
          {selectedKeys.length > 0 && (
            <Popconfirm
              title={`彻底删除选中的 ${selectedKeys.length} 个产品？`}
              description="将连同其全部 BOM/物料/成员/数据级联删除，不可恢复！"
              okText="确认删除"
              okButtonProps={{ danger: true }}
              cancelText="取消"
              onConfirm={onBulkDelete}
            >
              <Button danger loading={bulkDeleting}>
                {bulkDeleting
                  ? `删除中 ${delDone}/${selectedKeys.length}`
                  : `批量删除 (${selectedKeys.length})`}
              </Button>
            </Popconfirm>
          )}
          <Button icon={<DownloadOutlined />} loading={exporting} onClick={onExportTenants}>
            导出
          </Button>
          <ExcelImportButton<TenantFullImportRow, Map<string, string>>
            templateName="产品导入模板"
            templateHeaders={TENANT_FULL_TEMPLATE_HEADERS}
            templateSample={TENANT_FULL_TEMPLATE_SAMPLE}
            parse={parseTenantFullSheet}
            prepare={() => new Map<string, string>()}
            importOne={async (row, cache) => {
              const key = row.code.toLowerCase();
              let tid = cache.get(key);
              if (!tid) {
                const existing = (tenants.data ?? []).find(
                  (t) => String(t.code).trim().toLowerCase() === key,
                );
                tid =
                  existing?.tenant_id ??
                  (
                    await api.post<{ id: string }>('/tenants', {
                      code: row.code,
                      name: row.name,
                      customer_company: row.customer_company,
                    })
                  ).id;
                cache.set(key, tid);
              }
              if (row.member_email) {
                const u = emailToUser.get(row.member_email);
                if (!u)
                  throw new Error(`未找到账号：${row.member_email}（请先在「用户管理」创建）`);
                await api.post(`/tenants/${tid}/members`, { user_id: u.id, role: row.member_role });
              }
            }}
            onDone={() => tenants.refetch()}
            label="导入产品"
            previewColumns={[
              { title: '编码', dataIndex: 'code' },
              { title: '名称', dataIndex: 'name' },
              { title: '生产工厂', dataIndex: 'customer_company' },
              { title: '成员邮箱', dataIndex: 'member_email' },
              {
                title: '成员角色',
                dataIndex: 'member_role',
                editor: {
                  type: 'select',
                  allowClear: true,
                  options: [
                    { value: 'admin', label: '管理员' },
                    { value: 'editor', label: '编辑' },
                    { value: 'viewer', label: '审核员' },
                  ],
                },
              },
            ]}
            validateRow={(r) => {
              const email = String(r['member_email'] ?? '').toLowerCase();
              if (!email) return null; // 不填成员＝仅建产品
              if (!emailToUser.get(email)) return '成员邮箱在用户管理不存在（请先创建账号）';
              return null;
            }}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            新建产品
          </Button>
        </Space>
      </Space>

      <Table<TenantListItem>
        rowKey="tenant_id"
        loading={tenants.isLoading}
        dataSource={tenants.data ?? []}
        scroll={{ y: LIST_SCROLL_Y }}
        pagination={LIST_PAGINATION}
        rowSelection={{
          selectedRowKeys: selectedKeys,
          onChange: (keys: Key[]) => setSelectedKeys(keys.map(String)),
        }}
        columns={[
          { title: '编码', dataIndex: 'code', width: 180 },
          { title: '名称', dataIndex: 'name' },
          { title: '生产工厂', dataIndex: 'customer_company' },
          {
            title: '状态',
            dataIndex: 'status',
            width: 90,
            render: (s: string) => (
              <Tag color={s === 'active' ? 'green' : 'default'}>
                {s === 'active' ? '启用' : '归档'}
              </Tag>
            ),
          },
          { title: '创建时间', dataIndex: 'created_at', width: 170, render: fmtTime },
          {
            title: '操作',
            key: 'action',
            width: 130,
            render: (_: unknown, t: TenantListItem) => (
              <Space size="small">
                <Button
                  size="small"
                  type="link"
                  onClick={() => {
                    setEditTenant(t);
                    editForm.setFieldsValue(t);
                  }}
                >
                  编辑
                </Button>
                <Popconfirm
                  title={`彻底删除产品「${t.name}」？`}
                  description="将连同其全部 BOM/物料/成员/数据级联删除，不可恢复！"
                  okText="确认删除"
                  okButtonProps={{ danger: true }}
                  cancelText="取消"
                  onConfirm={() =>
                    deleteTenant.mutate(t.tenant_id, {
                      onSuccess: (r) => {
                        message.success(`已删除（清除 ${r.deleted} 项数据）`);
                        if (t.tenant_id === currentTenantId) clearTenant();
                      },
                      onError: (e) =>
                        message.error(
                          `删除失败：${e instanceof ApiError ? e.message : (e as Error).message}`,
                        ),
                    })
                  }
                >
                  <Button size="small" type="link" danger>
                    删除
                  </Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title="新建产品"
        open={createOpen}
        onOk={onCreate}
        confirmLoading={creating}
        onCancel={() => setCreateOpen(false)}
        width={620}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item name="code" label="编码" rules={[{ required: true, message: '请输入编码' }]}>
            <Input placeholder="如 K8S-PROJECT-A" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="customer_company" label="生产工厂（可选）">
            <AutoComplete
              options={factoryOptions}
              placeholder="选择或输入工厂名，输入过的以后可直接选"
              filterOption={(input, opt) => (opt?.value ?? '').includes(input)}
              allowClear
            />
          </Form.Item>

          <Form.Item
            label="成员（可选）"
            tooltip="从已有账号中选择，并指定其在本产品的角色；也可创建后再添加"
          >
            <Form.List name="members">
              {(fields, { add, remove }) => (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  {fields.map((field) => (
                    <Space key={field.key} align="baseline" style={{ width: '100%' }}>
                      <Form.Item
                        name={[field.name, 'user_id']}
                        rules={[{ required: true, message: '选择用户' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          showSearch
                          placeholder="选择用户"
                          style={{ width: 300 }}
                          optionFilterProp="label"
                          loading={users.isLoading}
                          options={(users.data ?? [])
                            .filter((u) => u.status === 'active')
                            .map((u) => ({
                              value: u.id,
                              label: `${u.name || '(未命名)'}（${u.email}）`,
                            }))}
                        />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'role']}
                        initialValue="admin"
                        rules={[{ required: true, message: '选择角色' }]}
                        style={{ marginBottom: 0 }}
                      >
                        <Select style={{ width: 110 }} options={MEMBER_ROLE_OPTIONS} />
                      </Form.Item>
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    </Space>
                  ))}
                  {/* 默认角色「管理员」：Form.List 内 Form.Item 的 initialValue 不生效，须由 add() 传入 */}
                  <Button
                    type="dashed"
                    onClick={() => add({ role: 'admin' })}
                    icon={<PlusOutlined />}
                    block
                  >
                    添加成员
                  </Button>
                </Space>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            name="group_ids"
            label="通知联系人（可选）"
            tooltip="从联系人分组导入该分组下所有联系人；创建后仍可在产品内单独增删"
          >
            <Select
              mode="multiple"
              allowClear
              placeholder="选择联系人分组导入"
              loading={groups.isLoading}
              optionFilterProp="label"
              options={(groups.data ?? []).map((g) => ({
                value: g.id,
                label: `${g.name}（${g.company}${
                  g.contact_count != null ? ` · ${g.contact_count} 人` : ''
                }）`,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑产品 ${editTenant?.code ?? ''}`}
        open={!!editTenant}
        onOk={onEdit}
        confirmLoading={updateTenant.isPending}
        onCancel={() => setEditTenant(null)}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item name="name" label="名称">
            <Input />
          </Form.Item>
          <Form.Item name="customer_company" label="生产工厂">
            <AutoComplete
              options={factoryOptions}
              placeholder="选择或输入工厂名"
              filterOption={(input, opt) => (opt?.value ?? '').includes(input)}
              allowClear
            />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              options={[
                { value: 'active', label: '启用' },
                { value: 'archived', label: '归档' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
